import { useSearchParams } from 'react-router-dom'
import { track } from '@/lib/analytics'
import { resolveOffer, OFFER_EN } from '@/data/offers'
import { useLocale } from '@/i18n/locale'
import { NavLink } from '@/components/NavLink'
import { CONTENT_UPDATED, formatContentDate } from '@/data/site-meta'
import './hero.css'

/**
 * Hero — первый экран: слова слева, молекула ДНК справа.
 *
 * Правая колонка на десктопе пустая НАМЕРЕННО. Секция стоит первой внутри
 * .dna-zone, фон у неё прозрачный, и справа от текста видна WebGL-молекула
 * (маска .dna-mask гасит левую часть под текст). Любая заливка секции или
 * картинка в правой колонке закроет единственный визуальный мотив страницы.
 * Поэтому плашка под текстом (tokens.css, «Подложка под текстом») здесь не
 * на всю секцию, как у остальных блоков, а только на колонке слов:
 * data-plate="off" на секции + класс plate на колонке.
 *
 * Контент подменяется по `?utm_offer=<key>` (src/data/offers.ts):
 *   - SSG рендерит DEFAULT_OFFER — для органики и ботов;
 *   - на edge (functions/_middleware.ts) HTMLRewriter меняет текст пяти
 *     слотов до отправки HTML, чтобы первый экран не мигал;
 *   - на клиенте useSearchParams подставляет тот же оффер при гидратации.
 *
 * data-offer-slot="eyebrow|h1|h1-sub|lede|cta" — якоря для HTMLRewriter.
 * Он умеет только заменить содержимое существующего узла, создать узел не
 * может, поэтому все пять слотов обязаны быть в разметке всегда. Имена
 * менять только синхронно с functions/_middleware.ts.
 *
 * Пруф-строка и дата не подменяются: лицо автора и факты одинаковы для
 * любого оффера.
 */

const COPY = {
  ru: {
    calc: 'Посчитать рост маржи на своих цифрах',
    author: 'Антон Ро Батень',
    casesN: '65',
    cases: 'проектов в 33 нишах и 9 странах',
    turnoverN: '$1.2B',
    turnover: 'совокупного оборота клиентов',
    acceptance: '70 % оплаты — по приёмке этапов',
    updated: 'Обновлено',
    // Ценность, которую получает клиент, — список Тони 2026-09-26, сведённый
    // в четыре группы, чтобы первый экран читался за пять секунд.
    // Основа продукта (Тони 2026-09-27): всё сразу — одна корпоративная
    // ИИ-среда, в ней общий контекст, работает вся команда, защищает
    // корпоративный тариф. Только то, что сайт уже обещает в продукте и FAQ.
    baseLabel: 'Основа — корпоративная ИИ-среда',
    base: [
      { title: 'Общий контекст', text: 'ИИ знает всю компанию, знания не уходят с сотрудником' },
      { title: 'Вся команда', text: 'каждый работает с ИИ и создаёт агентов под свои задачи' },
      { title: 'Корпоративный тариф', text: 'защищённые аккаунты, данные — ваши' },
    ],
    valuesLabel: 'Что получает бизнес · замер точки А и Б',
    values: [
      { title: 'Прибыль и стоимость компании', text: 'Растёт маржинальная прибыль, падают расходы, бизнес дорожает как актив.' },
      { title: 'Скорость и производительность', text: 'Больше задач делегируется агентам, исполнение ускоряется, команда успевает больше.' },
      { title: 'Прозрачность и порядок', text: 'Структура процессов и порядок в данных: весь бизнес в одном окне, без ручных отчётов.' },
      { title: 'Безопасность и независимость', text: 'Данные и наработки остаются в компании, и она не зависит от внешних экспертов и подрядчиков.' },
    ],
  },
  en: {
    calc: 'Run the margin numbers on your own inputs',
    author: 'Anton Ro Baten',
    casesN: '65',
    cases: 'projects across 33 industries and 9 countries',
    turnoverN: '$1.2B',
    turnover: 'combined client turnover',
    acceptance: '70% paid on stage acceptance',
    updated: 'Updated',
    baseLabel: 'The base: a corporate AI workspace',
    base: [
      { title: 'Shared context', text: 'AI knows the whole company; knowledge stays when people leave' },
      { title: 'The whole team', text: 'everyone works with AI and builds agents for their own tasks' },
      { title: 'Corporate plan', text: 'secure accounts, and the data is yours' },
    ],
    valuesLabel: 'What the business gets · measured A to B',
    values: [
      { title: 'Profit and company value', text: 'Margin grows, costs fall, and the business becomes worth more as an asset.' },
      { title: 'Speed and productivity', text: 'More tasks are delegated to agents, execution speeds up, and the team gets more done.' },
      { title: 'Transparency and order', text: 'Structured processes and clean data: the whole business in one window, no manual reports.' },
      { title: 'Security and independence', text: 'Data and know-how stay in the company, and it no longer depends on outside experts and contractors.' },
    ],
  },
} as const

export function Hero() {
  const [searchParams] = useSearchParams()
  const locale = useLocale()
  const isEn = locale === 'en'
  const t = COPY[locale]
  // На /en UTM-подмены нет: edge-middleware работает только на русской главной.
  const offer = isEn ? OFFER_EN : resolveOffer(searchParams.get('utm_offer'))

  return (
    <section data-block="hero" data-plate="off" className="relative pt-7 sm:pt-14 lg:pt-12 pb-12 lg:pb-14">
      <div className="hero__grid container-x grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
        <div className="hero__col plate lg:col-span-7">
          {/* Слот — на внутреннем span: HTMLRewriter затирает всё содержимое
              узла, и декоративная точка пропала бы вместе с текстом. */}
          <p className="flex items-baseline gap-2.5 font-mono text-[0.75rem] sm:text-small uppercase tracking-wider text-muted">
            {/* Точка привязана к первой строке: у длинных UTM-офферов eyebrow
                на телефоне переносится, и по центру она повисала бы между строк. */}
            <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-accent shrink-0 -translate-y-[0.15em]" />
            <span data-offer-slot="eyebrow">{offer.eyebrow}</span>
          </p>

          <h1 data-offer-slot="h1" className="hero__h1 mt-5 lg:mt-6 text-ink font-semibold">
            {offer.h1}
          </h1>

          {/* Подзаголовок вынесен из <h1>: вместе с ним заголовок был ~30
              слов при норме 5–12, и поисковик получал размытый H1. */}
          <p data-offer-slot="h1-sub" className="hero__sub mt-4 sm:mt-5 text-ink/80 max-w-[44ch]">
            {offer.h1Sub}
          </p>

          {/* Основа — на чём держится всё остальное: одна корпоративная
              ИИ-среда. Между подзаголовком и первым абзацем, чтобы оффер
              говорил «что это» раньше кнопки. Общий для всех офферов. */}
          <div className="mt-5 sm:mt-6">
            <p className="font-mono text-[0.75rem] sm:text-small uppercase tracking-wider text-muted">{t.baseLabel}</p>
            <ul className="mt-2.5 space-y-1.5">
              {t.base.map((b) => (
                <li key={b.title} className="flex gap-2.5 text-[0.9375rem] lg:text-base leading-snug text-ink/85">
                  <span aria-hidden className="mt-[0.5em] h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span>
                    <span className="font-semibold text-ink">{b.title}:</span> {b.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* У основного оффера абзаца нет (он в карточке условий), слот пуст и
              скрыт. Рекламные варианты вписывают сюда свой текст на edge. */}
          <p data-offer-slot="lede" className="mt-4 sm:mt-5 text-[0.9375rem] sm:text-body text-muted leading-[1.6] max-w-[62ch] empty:hidden">
            {offer.lede}
          </p>

          <div className="mt-7 sm:mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-7">
            <NavLink
              href="#final-cta"
              className="btn-cta no-underline text-center"
              onClick={() => track('hero_cta_click')}
            >
              <span data-offer-slot="cta">{offer.ctaText}</span>
            </NavLink>
            {/* Калькулятор на первом экране — мандат Тони 2026-08-03:
                обещание роста маржи обязано иметь под собой цифру, которую
                человек считает сам. */}
            <NavLink
              href="#calculator"
              className="inline-flex items-center gap-2 text-small font-medium text-accent no-underline hover:underline underline-offset-4"
              onClick={() => track('hero_calc_link_click')}
            >
              {t.calc}
              <span aria-hidden>→</span>
            </NavLink>
          </div>

          {/* Что получает бизнес — под кнопкой, а не над ней: заголовок и
              подзаголовок уже продают, кнопка остаётся в первом экране и на
              телефоне, а выгоды читает тот, кто решает, стоит ли жать. Блок
              общий для всех офферов: у рекламных вариантов своя первая
              строка, а ценность продукта та же. */}
          <div className="mt-8 lg:mt-10">
            <p className="font-mono text-[0.75rem] sm:text-small uppercase tracking-wider text-muted">{t.valuesLabel}</p>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
              {t.values.map((v, i) => (
                <li key={v.title} className="flex gap-3 border-t border-divider pt-4">
                  <span aria-hidden className="font-mono text-small tabular-nums text-muted pt-0.5">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <p className="text-body font-semibold text-ink leading-snug">{v.title}</p>
                    <p className="mt-1 text-small text-muted leading-relaxed">{v.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Пруф-строка: лицо автора слева, факты — лентой с переносом.
              Точки-разделители рисует hero.css, чтобы при переносе ни одна
              не осталась висеть в конце или начале строки. */}
          <div className="mt-10 lg:mt-12 pt-5 border-t border-divider flex items-start sm:items-center gap-3.5">
            <img
              src="/team/anton.jpg"
              alt=""
              width={40}
              height={40}
              loading="eager"
              decoding="async"
              className="w-10 h-10 rounded-full object-cover shrink-0"
              style={{ filter: 'grayscale(1) contrast(1.05)' }}
            />
            <ul className="hero__proof text-small text-muted">
              <li>
                {/* Страница автора есть только на русском — на /en имя без ссылки. */}
                {isEn ? (
                  <span className="text-ink font-medium">{t.author}</span>
                ) : (
                  <NavLink
                    href="/author/anton-robaten"
                    className="text-ink font-medium no-underline hover:underline underline-offset-4"
                  >
                    {t.author}
                  </NavLink>
                )}
              </li>
              <li>
                <NavLink
                  href="#cases"
                  className="text-muted underline decoration-divider underline-offset-4 hover:text-ink"
                >
                  <span className="font-mono text-ink">{t.casesN}</span> {t.cases}
                </NavLink>
              </li>
              <li>
                <span className="font-mono text-ink">{t.turnoverN}</span> {t.turnover}
              </li>
              <li>{t.acceptance}</li>
            </ul>
          </div>

          <p className="mt-4 font-mono text-[0.75rem] tracking-wide text-muted">
            {t.updated}:{' '}
            <time dateTime={CONTENT_UPDATED}>{formatContentDate(CONTENT_UPDATED, locale)}</time>
          </p>
        </div>

        {/* Место молекулы: пустая колонка держит сетку, контента в ней нет. */}
        <div className="hidden lg:block lg:col-span-5" aria-hidden />
      </div>
    </section>
  )
}
